/**
 * Unity API Service
 * Complete integration with Unity API
 * All endpoints for: Auth, Clients, Characters, Licenses, GMs, Server Status, Images
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Storage keys (only non-sensitive user info; tokens are in httpOnly cookies)
const STORAGE_KEYS = {
  USER: 'unity-auth-user'
};

// Session invalidation callback
let onSessionInvalidated = null;

// Flag to prevent callback during intentional logout
let isLoggingOut = false;

// Flag to indicate refresh is in progress (prevents 401 from triggering logout during refresh)
let isRefreshingToken = false;

/**
 * No-op: tokens are in httpOnly cookies managed by the browser/API.
 */
const ensureStoredExpiry = () => null;

/**
 * Set callback for session invalidation (401 errors)
 */
const setSessionInvalidationCallback = (callback) => {
  onSessionInvalidated = callback;
};

// ============================================
// TOKEN REFRESH MANAGER
// ============================================
/**
 * Robust Token Refresh Manager
 * Automatically refreshes JWT tokens before expiration with:
 * - Configurable refresh threshold (default: refresh at 80% of lifetime or 30s before expiry)
 * - Retry logic with exponential backoff on failures
 * - Handles browser tab visibility (refreshes immediately when tab becomes visible if needed)
 * - Graceful cleanup on logout
 * - Race condition prevention with mutex-like behavior
 */
const TokenRefreshManager = {
  // Internal state
  _refreshTimeoutId: null,
  _isRefreshing: false,
  _refreshPromise: null,
  _retryCount: 0,
  _maxRetries: 3,
  _retryDelayMs: 2000,
  _minRefreshBeforeExpiryMs: 60000, // Minimum 60 seconds before expiry
  _refreshThresholdPercent: 0.70, // Refresh at 70% of token lifetime
  _visibilityHandler: null,
  _focusHandler: null,
  _initialized: false,
  _lastVisibilityCheckTime: 0, // Debounce visibility checks

  /**
   * Initialize the refresh manager.
   * No-op for cookie-based auth (tokens are managed by browser/API).
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;
  },

  /**
   * Start refresh scheduling after login or successful refresh
   * @param {number} expiresInSeconds - Token lifetime in seconds from API response
   */
  startRefreshSchedule() {
    // No-op: tokens are in httpOnly cookies managed by the browser/API
    this.stopRefreshSchedule();
  },

  /**
   * Calculate when to schedule the refresh
   * @param {number} remainingMs - Remaining time until expiry in milliseconds
   * @returns {number} - Delay in milliseconds before refresh should occur
   */
  _calculateRefreshDelay(remainingMs) {
    // Refresh at threshold percentage of lifetime
    const percentageDelay = remainingMs * this._refreshThresholdPercent;
    
    // Only apply min-before-expiry if remaining time is larger than it
    const useMinDelay = remainingMs > this._minRefreshBeforeExpiryMs;
    const minBeforeExpiryDelay = useMinDelay
      ? remainingMs - this._minRefreshBeforeExpiryMs
      : remainingMs;
    
    let refreshDelay = Math.min(percentageDelay, minBeforeExpiryDelay);
    
    refreshDelay = Math.max(refreshDelay, 5000);
    
    if (remainingMs < 10000) {
      refreshDelay = 1000;
    }
    
    return refreshDelay;
  },

  /**
   * Schedule the next token refresh
   * @param {number} delayMs - Delay in milliseconds
   */
  _scheduleRefresh(delayMs) {
    // Clear any existing timeout
    if (this._refreshTimeoutId) {
      clearTimeout(this._refreshTimeoutId);
    }
    
    this._refreshTimeoutId = setTimeout(() => {
      this._performRefresh();
    }, delayMs);
  },

  /**
   * Perform the actual token refresh using refresh token rotation
   * Includes retry logic with exponential backoff
   */
  async _performRefresh() {
    // Prevent concurrent refresh attempts
    if (this._isRefreshing && this._refreshPromise) {
      return this._refreshPromise;
    }
    
    if (isLoggingOut) {
      return false;
    }
    
    this._isRefreshing = true;
    isRefreshingToken = true;
    
    this._refreshPromise = (async () => {
      try {
        // Refresh token is sent automatically via httpOnly cookie
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        
        const data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new Error(data?.message || data?.error || 'Token refresh failed');
        }
        
        this._retryCount = 0;
        return true;
      } catch (error) {
        console.error('[TokenRefresh] Error:', error.message);
        
        this._retryCount++;
        
        if (this._retryCount <= this._maxRetries) {
          const retryDelay = this._retryDelayMs * Math.pow(2, this._retryCount - 1);
          this._scheduleRefresh(retryDelay);
          return false;
        }
        
        console.error('[TokenRefresh] Max retries exceeded, invalidating session');
        
        if (getCurrentUser()) {
          this.stopRefreshSchedule();
          clearAuthData();
          
          if (onSessionInvalidated && !isLoggingOut) {
            onSessionInvalidated('Your session has expired. Please login again.');
          }
        }
        return false;
      } finally {
        this._isRefreshing = false;
        isRefreshingToken = false;
        this._refreshPromise = null;
      }
    })();
    
    return this._refreshPromise;
  },

  /**
   * Ensure token is fresh enough for upcoming requests
   * @param {number} minFreshMs - Minimum remaining lifetime in ms
   */
  async ensureFreshToken() {
    // Tokens are in httpOnly cookies managed by the browser/API
    return true;
  },

  /**
   * Force immediate refresh
   */
  refreshNow() {
    return this._performRefresh();
  },

  /**
   * Handle visibility change (tab becoming visible/hidden)
   * When tab becomes visible, check if token needs immediate refresh
   * Includes debounce to prevent rapid toggles from causing issues
   */
  _handleVisibilityChange() {
    // No-op: tokens are in httpOnly cookies managed by the browser/API
  },

  /**
   * Stop refresh scheduling (call on logout)
   */
  stopRefreshSchedule() {
    if (this._refreshTimeoutId) {
      clearTimeout(this._refreshTimeoutId);
      this._refreshTimeoutId = null;
    }
    this._retryCount = 0;
  },

  /**
   * Cleanup when app is unloaded
   */
  destroy() {
    this.stopRefreshSchedule();
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (this._focusHandler) {
      window.removeEventListener('focus', this._focusHandler);
      this._focusHandler = null;
    }
    this._initialized = false;
  }
};

/**
 * Get current auth token
 */
const getToken = () => null;

/**
 * Get current refresh token (httpOnly cookie, not accessible via JS)
 */
const getRefreshToken = () => null;

/**
 * Get current user from storage
 */
const getCurrentUser = () => {
  try {
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);
    return userJson ? JSON.parse(userJson) : null;
  } catch {
    return null;
  }
};

/**
 * Save auth data to storage
 */
const saveAuthData = (user) => {
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
};

/**
 * Clear auth data from storage
 */
const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.USER);
};

/**
 * Make API request with authentication
 */
const apiRequest = async (endpoint, options = {}) => {
  try {
    const isRetry = options.__isRetry === true;
    const fetchOptions = { ...options };
    delete fetchOptions.__isRetry;
    delete fetchOptions.__skipRefresh;

    const headers = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers
    };
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });
    
    // Handle image responses
    if (response.headers.get('content-type')?.includes('image/')) {
      if (response.ok) {
        const blob = await response.blob();
        return { success: true, blob };
      }
      return { success: false, error: 'Failed to load image' };
    }
    
    const data = await response.json();
    
    // Normalize error message - always return a string
    const normalizeError = (errorData) => {
      if (typeof errorData === 'string') return errorData;
      
      // Check for validation error with details array first
      if (errorData?.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
        // Extract messages from all details
        const messages = errorData.details.map(detail => detail.message).filter(Boolean);
        if (messages.length > 0) {
          return messages.join('; ');
        }
      }
      
      // Then check for nested error.details
      if (errorData?.error?.details && Array.isArray(errorData.error.details) && errorData.error.details.length > 0) {
        const messages = errorData.error.details.map(detail => detail.message).filter(Boolean);
        if (messages.length > 0) {
          return messages.join('; ');
        }
      }
      
      // Standard message extraction
      if (errorData?.message) return errorData.message;
      if (errorData?.error?.message) return errorData.error.message;
      
      return 'An error occurred';
    };
    
    if (response.status === 401) {
      if (isRefreshingToken) {
        return { success: false, error: normalizeError(data) || 'Token refresh failed', isRefreshError: true };
      }
      
      if (!isRetry) {
        const refreshed = await TokenRefreshManager.refreshNow();
        if (refreshed) {
          return apiRequest(endpoint, { ...fetchOptions, __isRetry: true });
        }
      }

      if (getCurrentUser()) {
        clearAuthData();
        TokenRefreshManager.stopRefreshSchedule();
        
        if (onSessionInvalidated && !isLoggingOut) {
          onSessionInvalidated('Your session has expired or was closed from another device.');
        }
      }
      
      return { success: false, error: normalizeError(data) || 'Session expired' };
    }
    
    if (response.status === 403) {
      return { 
        success: false, 
        error: normalizeError(data) || 'Access denied'
      };
    }
    
    // If response has success: false, extract error message
    if (data && data.success === false) {
      return {
        ...data,
        error: data.error ? normalizeError(data.error) : (data.message || 'An error occurred')
      };
    }
    
    return data;
  } catch (error) {
    console.error('[API] Request Error:', error);
    return { success: false, error: 'Could not connect to server. Please try again.' };
  }
};

// ============================================
// AUTH ENDPOINTS
// ============================================
export const AuthAPI = {
  /**
   * Login user
   */
  login: async (username, password, tokenType = 'site') => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, tokenType })
    });
    
    if (response.success && response.data) {
      const { client, tokenType: respTokenType, mustChangePassword, tempToken } = response.data;

      if (mustChangePassword) {
        return { success: true, mustChangePassword: true, tempToken, clientId: response.data.clientId, username: response.data.username };
      }

      if (!client) {
        return { success: false, error: 'Invalid response from server' };
      }
      
      const user = {
        id: client.id,
        username: client.username,
        email: client.email,
        nickname: client.nickname || '',
        isAdmin: client.isAdmin ?? false,
        isActive: client.isActive ?? true,
        lastLoginAt: client.lastLoginAt,
        createdAt: client.createdAt,
        tokenType: respTokenType || tokenType,
        loginTime: Date.now()
      };
      saveAuthData(user);
      
      return { success: true, user };
    }
    
    return response;
  },

  /**
   * Register new user
   */
  register: async (username, password, email) => {
    return apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, email })
    });
  },

  /**
   * Logout user
   */
  logout: async () => {
    TokenRefreshManager.stopRefreshSchedule();
    
    try {
      isLoggingOut = true;
      // Always call API so it can clear the httpOnly cookies
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('[API.Auth.logout] Error:', error);
    } finally {
      isLoggingOut = false;
    }
    
    clearAuthData();
    return true;
  },

  /**
   * Get current user profile
   */
  getProfile: async () => apiRequest('/auth/me'),

  /**
   * Get online status of current user
   */
  getOnlineStatus: async () => apiRequest('/clients/me/online'),

  /**
   * Get online status of any client by ID (Admin)
   */
  getOnlineStatusAdmin: async (clientId) => apiRequest(`/clients/me/online?clientId=${clientId}`),

  /**
   * Change password
   */
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  },

  /**
   * Refresh token manually using refresh token rotation
   */
  refreshToken: async () => {
    // Refresh token is sent automatically via httpOnly cookie
    return apiRequest('/auth/refresh', { method: 'POST' });
  },

  /**
   * Validate token
   */
  validateToken: async () => {
    return apiRequest('/auth/validate', { method: 'POST' });
  },

  /**
   * Request password reset (Forgot Password)
   * Sends email with reset link if account exists
   */
  forgotPassword: async (email) => {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password (min 6 characters)
   */
  resetPassword: async (token, newPassword) => {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  },

  /**
   * Verify email with 6-digit code
   * @param {string} user - Username
   * @param {string} code - 6-digit verification code
   */
  verifyEmail: async (user, code) => {
    return apiRequest('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ user, code })
    });
  },

  /**
   * Resend email verification
   * @param {string} user - Username
   */
  resendVerification: async (user) => {
    return apiRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ user })
    });
  },

  /**
   * Change temporary password (after admin reset)
   * @param {string} tempToken - Temporary token from login
   * @param {string} newPassword - New password
   */
  changeTempPassword: async (tempToken, newPassword) => {
    const response = await apiRequest('/auth/change-temp-password', {
      method: 'POST',
      body: JSON.stringify({ tempToken, newPassword })
    });

    if (response.success && response.data) {
      const { client } = response.data;

      const user = {
        id: client.id,
        username: client.username,
        email: client.email,
        nickname: client.nickname || '',
        isAdmin: client.isAdmin ?? false,
        isActive: client.isActive ?? true,
        lastLoginAt: client.lastLoginAt,
        createdAt: client.createdAt,
        tokenType: 'site',
        loginTime: Date.now()
      };
      saveAuthData(user);

      return { success: true, user };
    }

    return response;
  },

  // Helper methods
  getToken,
  getCurrentUser,
  isAuthenticated: () => !!getCurrentUser()
};

// ============================================
// CLIENTS ENDPOINTS
// ============================================
export const ClientsAPI = {
  /**
   * List all clients (Admin) with optional filters
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   * @param {string} [params.status] - 'active', 'banned', 'suspended', 'inactive'
   * @param {string} [params.hasCharacter] - 'yes', 'no'
   * @param {string} [params.tokenType] - 'app', 'site', 'both'
   * @param {string} [params.search] - Text search (3+ chars)
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - 'ASC' or 'DESC'
   */
  list: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.hasCharacter && params.hasCharacter !== 'all') query.set('hasCharacter', params.hasCharacter);
    if (params.tokenType && params.tokenType !== 'all') query.set('tokenType', params.tokenType);
    if (params.search) query.set('search', params.search);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    return apiRequest(`/clients?${query.toString()}`);
  },

  /**
   * Get online clients by type (Admin)
   * @param {string} type - 'app', 'site', or 'both'
   */
  getOnline: async (type = 'app') => apiRequest(`/clients/online?type=${type}`),

  /**
   * Get client by ID
   */
  getById: async (id) => apiRequest(`/clients/${id}`),

  /**
   * Update client
   */
  update: async (id, data) => {
    return apiRequest(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Activate client (Admin)
   */
  activate: async (id) => {
    return apiRequest(`/clients/${id}/activate`, { method: 'POST' });
  },

  /**
   * Deactivate/Ban client (Admin)
   * 
   * Examples:
   * 1. Permanent ban without reason:
   *    deactivate(5)
   * 
   * 2. Permanent ban with reason:
   *    deactivate(5, "Violated terms of use")
   * 
   * 3. Temporary suspension with reason and date:
   *    deactivate(5, "Inappropriate behavior", "2026-02-28T23:59:59.000Z")
   * 
   * 4. Temporary suspension without reason:
   *    deactivate(5, null, "2026-03-15T00:00:00.000Z")
   */
  deactivate: async (id, reason = null, until = null) => {
    const body = {}
    if (reason) body.reason = reason
    if (until) body.until = until
    
    return apiRequest(`/clients/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  /**
   * Delete client (Admin)
   */
  delete: async (id) => {
    return apiRequest(`/clients/${id}`, { method: 'DELETE' });
  },

  /**
   * Get client's characters
   */
  getCharacters: async (clientId) => apiRequest(`/clients/${clientId}/characters`),

  /**
   * Set all characters offline
   */
  setAllOffline: async (clientId) => {
    return apiRequest(`/clients/${clientId}/characters/offline-all`, { method: 'POST' });
  },

  /**
   * Get count of app-authenticated clients currently online (Public)
   */
  getAppOnlineCount: async () => apiRequest('/clients/app-online/count'),

  /**
   * Get count of site-authenticated clients currently online (Public)
   */
  getSiteOnlineCount: async () => apiRequest('/clients/site-online/count'),

  /**
   * Search clients by username or email (Admin)
   */
 
  search: async (query) => {
    if (!query || query.length < 3) {
      return { success: false, error: 'Search query must be at least 3 characters' };
    }
    return apiRequest(`/clients/search?q=${encodeURIComponent(query)}`);
  },

  /**
   * Update nickname
   * Owner only endpoint - each user can only change their own nickname
   */
  updateNickname: async (nickname) => {
    return apiRequest('/clients/me/nickname', {
      method: 'PATCH',
      body: JSON.stringify({ nickname })
    });
  },

  /**
   * Verify a client's email manually (Admin)
   */
  verifyEmail: async (id) => {
    return apiRequest(`/clients/${id}/verify-email`, { method: 'POST' });
  },

  /**
   * Reset a client's password (Admin)
   * @param {number} id - Client ID
   * @param {string} newPassword - New temporary password
   */
  resetPassword: async (id, newPassword) => {
    return apiRequest(`/clients/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword })
    });
  },

  /**
   * Revoke all sessions for a client - force logout (Admin)
   */
  revokeSessions: async (id) => {
    return apiRequest(`/clients/${id}/revoke-sessions`, { method: 'POST' });
  },

  /**
   * Get session history for a client (Admin)
   */
  getSessionsHistory: async (id) => {
    return apiRequest(`/clients/${id}/sessions`);
  },

  /**
   * Get config summary for a client (Admin)
   * Returns counts of characters, telegram, binds, etc.
   */
  getConfigSummary: async (id) => {
    return apiRequest(`/clients/${id}/config-summary`);
  },

  /**
   * Update license notes (Admin)
   */
  updateLicenseNotes: async (clientId, notes) => {
    return apiRequest(`/clients/${clientId}/license/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes })
    });
  }
};

// ============================================
// CHARACTERS ENDPOINTS
// ============================================
export const CharactersAPI = {
  /**
   * List characters (own or all for admin)
   */
  list: async (clientId = null) => {
    const url = clientId ? `/characters?clientId=${clientId}` : '/characters';
    return apiRequest(url);
  },

  /**
   * Get character by name and world
   */
  getByNameWorld: async (name, world) => {
    return apiRequest(`/characters/${encodeURIComponent(name)}/${encodeURIComponent(world)}`);
  },

  /**
   * Get character by ID
   */
  getById: async (id) => apiRequest(`/characters/${id}`),

  /**
   * Create or update character (upsert)
   */
  upsert: async (name, world, gameData = {}) => {
    return apiRequest('/characters', {
      method: 'POST',
      body: JSON.stringify({ name, world, gameData })
    });
  },

  /**
   * Update character
   */
  update: async (id, data) => {
    return apiRequest(`/characters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Update character game data (partial merge)
   */
  updateGameData: async (id, gameData) => {
    return apiRequest(`/characters/${id}/game-data`, {
      method: 'PATCH',
      body: JSON.stringify({ gameData })
    });
  },

  /**
   * Set character online/offline status
   */
  setStatus: async (id, isOnline) => {
    return apiRequest(`/characters/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isOnline })
    });
  },

  /**
   * Delete character by ID
   */
  delete: async (id) => {
    return apiRequest(`/characters/${id}`, { method: 'DELETE' });
  },

  /**
   * Delete character by name and world
   * @param {string} name - Character name
   * @param {string} world - World name/number
   * @param {string} clientId - Optional: Client ID (admin only, to delete another user's character)
   */
  deleteByNameWorld: async (name, world, clientId = null) => {
    const params = clientId ? `?clientId=${clientId}` : '';
    return apiRequest(`/characters/${encodeURIComponent(name)}/${encodeURIComponent(world)}${params}`, { 
      method: 'DELETE' 
    });
  },

  /**
   * Get online characters
   */
  getOnline: async () => apiRequest('/characters/online'),

  /**
   * Get world statistics (Admin)
   */
  getWorldStats: async () => apiRequest('/characters/stats/worlds'),

  /**
   * Get online characters count (Public)
   */
  getOnlineCount: async () => apiRequest('/characters/online/count')
};

// ============================================
// LICENSES ENDPOINTS
// ============================================
export const LicensesAPI = {
  /**
   * List all licenses (Admin)
   */
  list: async (page = 1, limit = 10) => {
    return apiRequest(`/licenses?page=${page}&limit=${limit}`);
  },

  /**
   * Get client's license
   */
  getClientLicense: async (clientId) => apiRequest(`/clients/${clientId}/license`),

  /**
   * Create or update license (Admin)
   */
  upsert: async (clientId, tabs, notes = null) => {
    return apiRequest(`/clients/${clientId}/license`, {
      method: 'PUT',
      body: JSON.stringify({ tabs, notes })
    });
  },

  /**
   * Add permanent tabs (Admin)
   */
  addTabs: async (clientId, amount) => {
    return apiRequest(`/clients/${clientId}/license/add-tabs`, {
      method: 'POST',
      body: JSON.stringify({ amount })
    });
  },

  /**
   * Remove permanent tabs (Admin)
   */
  removeTabs: async (clientId, amount) => {
    return apiRequest(`/clients/${clientId}/license/remove-tabs`, {
      method: 'POST',
      body: JSON.stringify({ amount })
    });
  },

  /**
   * Add NewTab package (Admin)
   */
  addNewTab: async (clientId, quantity, expiresAt, description = null, notes = null) => {
    return apiRequest(`/clients/${clientId}/newtabs`, {
      method: 'POST',
      body: JSON.stringify({ quantity, expiresAt, description, notes })
    });
  },

  /**
   * Update NewTab package (Admin)
   */
  updateNewTab: async (newtabId, data) => {
    return apiRequest(`/newtabs/${newtabId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Delete NewTab package (Admin)
   */
  deleteNewTab: async (newtabId) => {
    return apiRequest(`/newtabs/${newtabId}`, { method: 'DELETE' });
  },

  /**
   * Delete client's license (Admin)
   */
  deleteLicense: async (clientId) => {
    return apiRequest(`/clients/${clientId}/license`, { method: 'DELETE' });
  },

  /**
   * Get expired packages (Admin)
   */
  getExpired: async () => apiRequest('/licenses/expired'),

  /**
   * Cleanup expired packages (Admin)
   */
  cleanup: async () => {
    return apiRequest('/licenses/cleanup', { method: 'POST' });
  }
};

// ============================================
// GAME MASTERS ENDPOINTS
// ============================================
export const GMsAPI = {
  /**
   * List all GMs (Public)
   */
  list: async (page = 1, limit = 10) => {
    return apiRequest(`/gms?page=${page}&limit=${limit}`);
  },

  /**
   * Get recent GMs (Public)
   */
  getRecent: async (minutes = 30) => {
    return apiRequest(`/gms/recent?minutes=${minutes}`);
  },

  /**
   * Get GM statistics (Public)
   */
  getStats: async () => apiRequest('/gms/stats'),

  /**
   * Get GM by ID (Public)
   */
  getById: async (id) => apiRequest(`/gms/${id}`),

  /**
   * Get GM by name (Public)
   */
  getByName: async (name) => apiRequest(`/gms/name/${encodeURIComponent(name)}`),

  /**
   * Report GM sighting (Authenticated)
   */
  report: async (name, location) => {
    return apiRequest('/gms/report', {
      method: 'POST',
      body: JSON.stringify({ name, location })
    });
  },

  /**
   * Create/Update GM (Admin)
   */
  upsert: async (data) => {
    return apiRequest('/gms', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Update GM (Admin)
   */
  update: async (id, data) => {
    return apiRequest(`/gms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Delete GM (Admin)
   */
  delete: async (id) => {
    return apiRequest(`/gms/${id}`, { method: 'DELETE' });
  }
};

// ============================================
// SERVER STATUS ENDPOINTS
// ============================================
export const ServerAPI = {
  /**
   * Get current status (Public)
   */
  getStatus: async () => apiRequest('/server/status'),

  /**
   * Get restrictions (Public)
   */
  getRestrictions: async () => apiRequest('/server/restrictions'),

  /**
   * Get status history (Public)
   */
  getHistory: async (limit = 10) => {
    return apiRequest(`/server/history?limit=${limit}`);
  },

  /**
   * Update status (Admin)
   */
  updateStatus: async (status, message = null, estimatedRecoveryAt = null) => {
    return apiRequest('/server/status', {
      method: 'PUT',
      body: JSON.stringify({ status, message, estimatedRecoveryAt })
    });
  },

  /**
   * Set online (Admin)
   */
  setOnline: async (message = null) => {
    return apiRequest('/server/online', {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  },

  /**
   * Set offline (Admin)
   */
  setOffline: async (message = null, estimatedRecoveryAt = null) => {
    return apiRequest('/server/offline', {
      method: 'POST',
      body: JSON.stringify({ message, estimatedRecoveryAt })
    });
  },

  /**
   * Set maintenance (Admin)
   */
  setMaintenance: async (message = null, estimatedRecoveryAt = null) => {
    return apiRequest('/server/maintenance', {
      method: 'POST',
      body: JSON.stringify({ message, estimatedRecoveryAt })
    });
  },

  /**
   * Set updating (Admin)
   */
  setUpdating: async (message = null, estimatedRecoveryAt = null) => {
    return apiRequest('/server/updating', {
      method: 'POST',
      body: JSON.stringify({ message, estimatedRecoveryAt })
    });
  },

  /**
   * Set degraded (Admin)
   */
  setDegraded: async (message = null) => {
    return apiRequest('/server/degraded', {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  },

  /**
   * Set restarting (Admin)
   */
  setRestarting: async (message = null, estimatedRecoveryAt = null) => {
    return apiRequest('/server/restarting', {
      method: 'POST',
      body: JSON.stringify({ message, estimatedRecoveryAt })
    });
  },

  /**
   * Get world stats (Public)
   */
  worldStats: async () => apiRequest('/server/world-stats'),
};

// ============================================
// IMAGES ENDPOINTS
// ============================================
export const ImagesAPI = {
  /**
   * List client's images
   */
  list: async (clientId) => apiRequest(`/clients/${clientId}/images`),

  /**
   * Get image URL
   */
  getUrl: (clientId, filename) => `${API_BASE_URL}/clients/${clientId}/images/${filename}`,

  /**
   * Get image with auth
   */
  get: async (clientId, filename) => {
    const url = `${API_BASE_URL}/clients/${clientId}/images/${filename}`;
    
    try {
      const response = await fetch(url, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const blob = await response.blob();
        return { success: true, blob };
      }
      
      const errorText = await response.text();
      console.error('[API.Images.get] ❌ Erro HTTP:', {
        status: response.status,
        error: errorText
      });
      
      return { success: false, error: errorText };
    } catch (err) {
      console.error('[API.Images.get] ❌ Exceção:', {
        message: err.message,
        stack: err.stack
      });
      return { success: false, error: err.message };
    }
  }
};

// ============================================
// BOT MANAGEMENT ENDPOINTS (New Architecture)
// ============================================
export const BotAPI = {
  // ============================================
  // PUBLIC ROUTES (no authentication required)
  // ============================================

  /**
   * Get info about the latest published version
   * Returns: { version, changelog, mandatory, publishedAt, hasInstaller, hasUpdate, installerSize, installerSizeFormatted }
   */
  getLatest: async () => apiRequest('/bot/latest'),

  /**
   * Get installer download URL (latest published version)
   * Use this in <a href> or window.location for direct download
   */
  getInstallerUrl: () => `${API_BASE_URL}/bot/installer`,

  // ============================================
  // AUTHENTICATED ROUTES (requires JWT token)
  // ============================================

  /**
   * Check for available updates
   * @param {string} currentVersion - Current version of the bot (e.g. "1.2.7.7")
   * Returns: { updateAvailable, currentVersion, latestVersion, mandatory, changelog, checksum, size, sizeFormatted, downloadUrl }
   */
  checkUpdate: async (currentVersion) => apiRequest(`/bot/update-check?current=${encodeURIComponent(currentVersion)}`),

  /**
   * Get update download URL for a specific version
   * @param {string} version - Version to download (e.g. "1.2.7.8")
   */
  getUpdateDownloadUrl: (version) => `${API_BASE_URL}/bot/updates/${encodeURIComponent(version)}/download`,

  /**
   * Get full changelog (all published versions) or for a specific version
   * @param {string} [version] - Optional version to get changelog for
   * Returns: { totalEntries, changelog: [{ version, changes, mandatory, date }] }
   */
  getChangelog: async (version = null) => {
    if (version) {
      return apiRequest(`/bot/changelog/${encodeURIComponent(version)}`);
    }
    return apiRequest('/bot/changelog');
  },

  // ============================================
  // ADMIN ROUTES (requires JWT token + isAdmin)
  // ============================================

  /**
   * Upload the unique installer (not versioned, replaces previous)
   * @param {File} installerFile - .exe file
   * Returns: { filename, size, sizeFormatted, checksum, uploadedAt }
   */
  uploadInstaller: async (installerFile) => {
    const formData = new FormData();
    formData.append('installer', installerFile);

    const response = await fetch(`${API_BASE_URL}/bot/installer/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    return response.json();
  },

  /**
   * List all versions (including unpublished drafts)
   * Returns: { totalVersions, versions: [{ id, version, changelog, published, hasInstaller, hasUpdate, ... }] }
   */
  getAllVersions: async () => apiRequest('/bot/versions'),

  /**
   * Create a new version (atomic: update files + metadata + checksum)
   * @param {Object} versionData - { version, changelog, mandatory, files }
   * - version: string (required) - e.g. "1.2.7.8"
   * - changelog: array (required) - e.g. ["Fix bug X", "New feature Y"]
   * - mandatory: boolean (optional) - default: false
   * - files: File[] (required) - Files for update package (will be zipped)
   * Note: Installer is now managed separately via uploadInstaller()
   */
  createVersion: async (versionData) => {
    const formData = new FormData();
    
    // Required fields
    formData.append('version', versionData.version);
    formData.append('changelog', JSON.stringify(versionData.changelog));
    
    // Optional fields
    if (versionData.mandatory !== undefined) {
      formData.append('mandatory', String(versionData.mandatory));
    }
    
    // Update files (required)
    if (versionData.files && versionData.files.length > 0) {
      for (const file of versionData.files) {
        formData.append('files', file);
      }
    }
    console.log('[API.Bot.createVersion] Enviando dados da versão:', {
      version: versionData.version,
      changelog: versionData.changelog,
      mandatory: versionData.mandatory,
      files: versionData.files ? versionData.files.map(f => ({ name: f.name, size: f.size })) : []
    });

    const response = await fetch(`${API_BASE_URL}/bot/versions`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    return response.json();
  },

  /**
   * Publish a version (make it publicly available)
   * @param {number} versionId - ID of the version to publish
   */
  publishVersion: async (versionId) => {
    return apiRequest(`/bot/versions/${versionId}/publish`, {
      method: 'POST'
    });
  },

  /**
   * Unpublish a version (remove from public access)
   * @param {number} versionId - ID of the version to unpublish
   */
  unpublishVersion: async (versionId) => {
    return apiRequest(`/bot/versions/${versionId}/unpublish`, {
      method: 'POST'
    });
  },

  /**
   * Edit changelog of a version
   * @param {number} versionId - ID of the version
   * @param {array} changelog - New changelog array ["Change 1", "Change 2"]
   */
  editChangelog: async (versionId, changelog) => {
    return apiRequest(`/bot/versions/${versionId}/changelog`, {
      method: 'PUT',
      body: JSON.stringify({ changelog })
    });
  },

  /**
   * Delete a version permanently (removes files and DB record)
   * @param {number} versionId - ID of the version to delete
   */
  deleteVersion: async (versionId) => {
    return apiRequest(`/bot/versions/${versionId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// RESOURCES API
// ============================================
const ResourcesAPI = {
  /**
   * Get the current manifest
   * @returns {Promise} Response with manifest data
   */
  getManifest: async () => {
    return apiRequest('/resources/manifest', {
      method: 'GET'
    });
  },

  /**
   * Get all files organized by category
   * @returns {Promise} Response with files by category
   */
  getFiles: async () => {
    return apiRequest('/resources/files', {
      method: 'GET'
    });
  },

  /**
   * Upload files to resources
   * Supports 3 modes:
   * - Single category: uploadFiles(files, 'lib')
   * - Multiple categories with fieldname: uploadFiles(filesByCategory) where filesByCategory = { java: [file1], lib: [file2] }
   * - Multiple categories with array: uploadFiles(files, categories) where categories = ['java', 'lib']
   * 
   * @param {File|File[]|Object} files - Files to upload
   * @param {string|string[]} category - Category name(s) or array of categories
   * @returns {Promise} Response with upload results including successful and failed arrays
   */
  uploadFiles: async (files, category = null) => {
    const formData = new FormData();
    
    // Mode 1: Object with categories as keys { java: [files], lib: [files] }
    if (typeof files === 'object' && !Array.isArray(files) && !(files instanceof File)) {
      Object.entries(files).forEach(([cat, catFiles]) => {
        const fileArray = Array.isArray(catFiles) ? catFiles : [catFiles];
        fileArray.forEach(file => {
          formData.append(`files[${cat}]`, file);
        });
      });
    }
    // Mode 2: Array of files with array of categories
    else if (Array.isArray(category)) {
      const fileArray = Array.isArray(files) ? files : [files];
      fileArray.forEach(file => {
        formData.append('files', file);
      });
      formData.append('categories', JSON.stringify(category));
    }
    // Mode 3: Single category (backward compatible)
    else {
      const fileArray = Array.isArray(files) ? files : [files];
      fileArray.forEach(file => {
        formData.append('files', file);
      });
      if (category) {
        formData.append('category', category);
      }
    }

    const response = await fetch(`${API_BASE_URL}/resources/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    return response.json();
  },

  /**
   * Delete a file from a category
   * @param {string} category - Category name
   * @param {string} filename - Filename to delete
   * @returns {Promise} Response with deletion result
   */
  deleteFile: async (category, filename) => {
    return apiRequest(`/resources/files/${category}/${filename}`, {
      method: 'DELETE'
    });
  },

  /**
   * Delete ALL files from a category
   * @param {string} category - Category name
   * @returns {Promise} Response with deletion result including deletedCount
   */
  deleteCategory: async (category) => {
    return apiRequest(`/resources/files/${category}`, {
      method: 'DELETE'
    });
  },

  /**
   * Synchronize filesystem with database
   * Scans filesystem and updates database accordingly
   * @returns {Promise} Response with updated manifest
   */
  syncFilesystem: async () => {
    return apiRequest('/resources/sync', {
      method: 'POST'
    });
  },

  /**
   * Download resources as ZIP
   * @param {Object} files - Optional: specific files to download by category
   * @returns {Promise} Downloads ZIP file
   */
  downloadResources: async (files = null) => {
    const response = await fetch(`${API_BASE_URL}/resources/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: files ? JSON.stringify({ files }) : JSON.stringify({})
    });
    console.log(`body da mensagem enviada para downloadResources: ${files ? JSON.stringify({ files }) : '{}'}`);

    if (!response.ok) {
      throw new Error('Failed to download resources');
    }

    // Get filename from Content-Disposition header or use default
    const disposition = response.headers.get('Content-Disposition');
    let filename = 'resources.zip';
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/"/g, '');
    }

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    try { document.body.removeChild(a); } catch (e) {}
    
    return { success: true };
  },

  /**
   * Get available categories
   * @returns {Promise} Response with categories info
   */
  getCategories: async () => {
    return apiRequest('/resources/categories', {
      method: 'GET'
    });
  },

  // ── MAP ──────────────────────────────────────────────────────────────

  /**
   * Get map system status (Admin)
   * @returns {Promise} mapExists, sizes, tilesInQueue, mapLastModified
   */
  getMapStatus: async () => {
    return apiRequest('/resources/map/status', { method: 'GET' });
  },

  /**
   * Upload / replace the authoritative map.bin (Admin)
   * @param {File} file - Binary map file
   * @returns {Promise} { sizeBytes, sizeFormatted }
   */
  uploadMap: async (file) => {
    const response = await fetch(`${API_BASE_URL}/resources/map`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      credentials: 'include',
      body: file
    });
    return response.json();
  },

  /**
   * Download current map.bin (Admin)
   * @returns {Promise} Triggers browser download
   */
  downloadMap: async () => {
    const response = await fetch(`${API_BASE_URL}/resources/map`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to download map');
    }

    const disposition = response.headers.get('Content-Disposition');
    let filename = 'map.bin';
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/"/g, '');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    try { document.body.removeChild(a); } catch (e) {}

    return { success: true };
  }
};

// ============================================
// BOT CONFIGS API (User's own bot configurations)
// ============================================
const BotConfigsAPI = {
  // ── Telegram Configs ──────────────────────────────────────────
  upsertTelegram: async (tabName, configData) => {
    return apiRequest('/bot-configs/telegram', {
      method: 'PUT',
      body: JSON.stringify({ tabName, configData })
    });
  },
  getAllTelegram: async () => apiRequest('/bot-configs/telegram'),
  getTelegramByTab: async (tabName) => apiRequest(`/bot-configs/telegram/${tabName}`),
  deleteTelegram: async (tabName) => apiRequest(`/bot-configs/telegram/${tabName}`, { method: 'DELETE' }),

  // ── Bind Configs ──────────────────────────────────────────────
  upsertBind: async (name, world, type, path) => {
    return apiRequest('/bot-configs/binds', {
      method: 'PUT',
      body: JSON.stringify({ name, world, type, path })
    });
  },
  getAllBinds: async (type = null) => {
    const query = type ? `?type=${type}` : '';
    return apiRequest(`/bot-configs/binds${query}`);
  },
  getBind: async (name, world, type) => apiRequest(`/bot-configs/binds/${encodeURIComponent(name)}/${encodeURIComponent(world)}/${type}`),
  deleteBind: async (name, world, type) => apiRequest(`/bot-configs/binds/${encodeURIComponent(name)}/${encodeURIComponent(world)}/${type}`, { method: 'DELETE' }),
  deleteBindsByCharacter: async (name, world) => apiRequest(`/bot-configs/binds/${encodeURIComponent(name)}/${encodeURIComponent(world)}`, { method: 'DELETE' }),

  // ── Unreal Credentials ────────────────────────────────────────
  upsertUnrealCredential: async (name, world, password) => {
    return apiRequest('/bot-configs/unreal-credentials', {
      method: 'PUT',
      body: JSON.stringify({ name, world, password })
    });
  },
  getAllUnrealCredentials: async () => apiRequest('/bot-configs/unreal-credentials'),
  getUnrealCredential: async (name, world) => apiRequest(`/bot-configs/unreal-credentials/${encodeURIComponent(name)}/${encodeURIComponent(world)}`),
  deleteUnrealCredential: async (name, world) => apiRequest(`/bot-configs/unreal-credentials/${encodeURIComponent(name)}/${encodeURIComponent(world)}`, { method: 'DELETE' }),

  // ── Life Insurance ────────────────────────────────────────────
  upsertLifeInsurance: async (characterName, world, data) => {
    return apiRequest('/bot-configs/life-insurance', {
      method: 'PUT',
      body: JSON.stringify({ characterName, world, data })
    });
  },
  getAllLifeInsurance: async () => apiRequest('/bot-configs/life-insurance'),
  getLifeInsurance: async (characterName, world) => apiRequest(`/bot-configs/life-insurance/${encodeURIComponent(characterName)}/${encodeURIComponent(world)}`),
  deleteLifeInsurance: async (characterName, world) => apiRequest(`/bot-configs/life-insurance/${encodeURIComponent(characterName)}/${encodeURIComponent(world)}`, { method: 'DELETE' }),
};

// ============================================
// ADMIN STATS API
// ============================================
const AdminAPI = {
  getStats: async () => apiRequest('/admin/stats'),
};

// ============================================
// ============================================
// ANNOUNCEMENTS ENDPOINTS
// ============================================
export const AnnouncementsAPI = {
  list: async (page = 1, limit = 20, activeOnly = true, type = null) => {
    let url = `/announcements?page=${page}&limit=${limit}&activeOnly=${activeOnly}`;
    if (type) url += `&type=${type}`;
    return apiRequest(url);
  },
  getById: async (id) => apiRequest(`/announcements/${id}`),
  create: async (data) => apiRequest('/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: async (id, data) => apiRequest(`/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: async (id) => apiRequest(`/announcements/${id}`, { method: 'DELETE' }),
};

// COMBINED API EXPORT
// ============================================
const API = {
  Auth: AuthAPI,
  Clients: ClientsAPI,
  Characters: CharactersAPI,
  Licenses: LicensesAPI,
  GMs: GMsAPI,
  Server: ServerAPI,
  Images: ImagesAPI,
  Bot: BotAPI,
  Resources: ResourcesAPI,
  Admin: AdminAPI,
  BotConfigs: BotConfigsAPI,
  Announcements: AnnouncementsAPI,
  
  // Quick access helpers
  getToken,
  getCurrentUser,
  isAuthenticated: AuthAPI.isAuthenticated,
  setSessionInvalidationCallback,
  
  /**
   * Initialize the API service (call on app startup)
   * Sets up automatic token refresh if user is logged in
   */
  init: () => {
    TokenRefreshManager.init();
  }
};

export default API;
